import React from "react";
import { Link } from "react-router-dom";
import { Quote, ArrowRight } from "lucide-react";

// ScrollingReference — vertical, infinite, auto-scrolling marquee of the
// Maureen Kelley (Nelson County, VA) public-sector reference letter.
//
// Used in the hero right column of /tourism/dmos. Designed to fit a
// narrow column (~360px wide) and a tall hero (~520px). Pauses on hover.

const CARDS = [
  {
    eyebrow: "Performance rating · Nelson County, VA",
    big: "10",
    suffix: "/ 10",
    body:
      "Maureen Kelley, Director of Economic Development & Tourism, Nelson County, a multi-year client.",
    kind: "score",
  },
  {
    eyebrow: "On public-sector work",
    body:
      "\u201CAnne Flinn Advertising consistently delivers high-quality creative products, strategic guidance, and measurable results. They are particularly effective at working with public-sector organizations, navigating stakeholder considerations, and developing messaging that balances policy objectives with public understanding.\u201D",
    kind: "quote",
  },
  {
    eyebrow: "Would you rehire?",
    body:
      "\u201CAbsolutely. I would not hesitate to hire them again and would strongly recommend them to other organizations.\u201D",
    kind: "quote",
  },
  {
    eyebrow: "Did they meet timelines?",
    body:
      "\u201CConsistently met project deadlines and often exceeds expectations when working under compressed timelines.\u201D",
    kind: "quote",
  },
  {
    eyebrow: "Greatest strength",
    body:
      "\u201CTranslate complex issues into clear and compelling messages, and develop communication strategies that resonate with diverse audiences.\u201D",
    kind: "quote",
  },
  {
    eyebrow: "Outcomes delivered",
    body:
      "\u201CStrengthened brand awareness, increased visitor engagement, improved marketing effectiveness, and navigated changing tourism trends.\u201D",
    kind: "quote",
  },
];

const Card = ({ card }) => {
  if (card.kind === "score") {
    return (
      <div className="rounded-2xl border border-[var(--af-line)] bg-[var(--af-navy-soft)]/60 p-5">
        <div className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-cream)]/55 mb-2">
          {card.eyebrow}
        </div>
        <div className="flex items-baseline gap-2 mb-3">
          <div className="font-display text-6xl text-[var(--af-lime)] leading-none">
            {card.big}
          </div>
          <div className="text-xl text-[var(--af-cream)]/55">{card.suffix}</div>
        </div>
        <div className="text-xs text-[var(--af-cream)]/75 leading-relaxed">
          {card.body}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[var(--af-line)] bg-[var(--af-navy-soft)]/60 p-5">
      <div className="flex items-center gap-2 mb-2">
        <Quote size={14} className="text-[var(--af-lime)]" />
        <div className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-lime)]">
          {card.eyebrow}
        </div>
      </div>
      <p className="text-sm text-[var(--af-cream)]/85 italic leading-relaxed">
        {card.body}
      </p>
    </div>
  );
};

const ScrollingReference = () => {
  // Duplicate the list once so the CSS keyframe can translate from 0 to -50%
  // and produce a seamless infinite loop.
  const loop = [...CARDS, ...CARDS];

  return (
    <div
      className="fade-up"
      style={{ animationDelay: ".15s" }}
      data-testid="scrolling-reference"
    >
      <div className="surface overflow-hidden p-0">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <div className="eyebrow">Verified Public-Sector Reference</div>
          <span className="text-[10px] uppercase tracking-widest text-[var(--af-lime)]/80">
            Live · Live · Live
          </span>
        </div>

        {/* Marquee viewport */}
        <div
          className="relative overflow-hidden"
          style={{
            maskImage:
              "linear-gradient(to bottom, transparent 0, #000 12%, #000 88%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0, #000 12%, #000 88%, transparent 100%)",
            height: "440px",
          }}
        >
          <div
            className="flex flex-col gap-4 px-5 py-4 sr-marquee"
            data-testid="scrolling-reference-track"
          >
            {loop.map((c, i) => (
              <Card key={i} card={c} />
            ))}
          </div>
        </div>

        <div className="px-5 pt-3 pb-5 border-t border-[var(--af-line)]/60">
          <Link
            to="/tourism/case-studies"
            className="inline-flex items-center gap-1.5 text-xs text-[var(--af-lime)] hover:underline underline-offset-4 font-medium"
            data-testid="scrolling-reference-cta"
          >
            Read the full reference letter <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      {/* Scoped keyframes for the vertical marquee. Pause on hover. */}
      <style>{`
        .sr-marquee {
          animation: sr-scroll 38s linear infinite;
          will-change: transform;
        }
        [data-testid="scrolling-reference"]:hover .sr-marquee {
          animation-play-state: paused;
        }
        @keyframes sr-scroll {
          from { transform: translateY(0); }
          to   { transform: translateY(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .sr-marquee { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default ScrollingReference;
